export const MESSAGE_PERFORMANCE_LABEL = 'Performance by current message set · Prospect DMs only';
export const MESSAGE_SLOT_LABEL = 'Usage by message slot · Prospect DMs only';
export interface CurrentMessageRow {
  tier: string;
  copy_set_id: string | null;
  message_version: string | null;
  copy_set_name: string;
  draft: boolean;
  one_off: boolean;
  archived: boolean;
  people_messaged: number;
  replied: number;
  info_replied: number;
  calls_booked: number;
}
export type MessageSlotRow = Omit<
  CurrentMessageRow,
  'replied' | 'info_replied' | 'calls_booked'
> & {
  message_slot: number | null;
  dms_sent: number;
};
export interface CurrentMessageReport {
  scope: string;
  performance_label: typeof MESSAGE_PERFORMANCE_LABEL;
  performance_policy: string;
  slot_label: typeof MESSAGE_SLOT_LABEL;
  slot_policy: string;
  messages: CurrentMessageRow[];
  slots: MessageSlotRow[];
  attribution_needs_review: number;
}
const object = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);
const nullableText = (v: unknown) => v === null || typeof v === 'string';
const count = (v: unknown) => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
function identity(v: unknown): v is Record<string, unknown> {
  return (
    object(v) &&
    ['tier1', 'tier2', 'trash'].includes(String(v.tier)) &&
    nullableText(v.copy_set_id) &&
    nullableText(v.message_version) &&
    typeof v.copy_set_name === 'string' &&
    typeof v.draft === 'boolean' &&
    typeof v.one_off === 'boolean' &&
    typeof v.archived === 'boolean' &&
    count(v.people_messaged)
  );
}
function projectIdentity(
  row: Record<string, unknown>,
): Omit<CurrentMessageRow, 'replied' | 'info_replied' | 'calls_booked'> {
  return {
    tier: String(row.tier),
    copy_set_id: row.copy_set_id as string | null,
    message_version: row.message_version as string | null,
    copy_set_name: String(row.copy_set_name),
    draft: row.draft as boolean,
    one_off: row.one_off as boolean,
    archived: row.archived as boolean,
    people_messaged: Number(row.people_messaged),
  };
}
export function decodeMessageReport(v: unknown): CurrentMessageReport {
  if (
    !object(v) ||
    v.scope !== 'Current prospect records only' ||
    v.performance_label !== MESSAGE_PERFORMANCE_LABEL ||
    v.slot_label !== MESSAGE_SLOT_LABEL ||
    typeof v.performance_policy !== 'string' ||
    typeof v.slot_policy !== 'string' ||
    !count(v.attribution_needs_review) ||
    !Array.isArray(v.messages) ||
    !v.messages.every(
      (r) => identity(r) && count(r.replied) && count(r.info_replied) && count(r.calls_booked),
    ) ||
    !Array.isArray(v.slots) ||
    !v.slots.every(
      (r) =>
        identity(r) &&
        count(r.dms_sent) &&
        (r.message_slot === null ||
          (Number.isInteger(r.message_slot) &&
            Number(r.message_slot) >= 1 &&
            Number(r.message_slot) <= 8)),
    )
  )
    throw new Error('message_report_invalid');
  return {
    scope: v.scope,
    performance_label: v.performance_label,
    performance_policy: v.performance_policy,
    slot_label: v.slot_label,
    slot_policy: v.slot_policy,
    attribution_needs_review: Number(v.attribution_needs_review),
    messages: v.messages.map((value: unknown) => {
      const row = value as Record<string, unknown>;
      return {
        ...projectIdentity(row),
        replied: Number(row.replied),
        info_replied: Number(row.info_replied),
        calls_booked: Number(row.calls_booked),
      };
    }),
    slots: v.slots.map((value: unknown) => {
      const row = value as Record<string, unknown>;
      return {
        ...projectIdentity(row),
        message_slot: row.message_slot as number | null,
        dms_sent: Number(row.dms_sent),
      };
    }),
  };
}
export function currentMessageLabel(
  row: Pick<CurrentMessageRow, 'copy_set_name' | 'draft' | 'archived'>,
): string {
  return `${row.copy_set_name}${row.draft ? ' (Draft)' : ''}${row.archived ? ' (archived)' : ''}`;
}
/** Full immutable ID: even versions sharing a name or UUID prefix stay distinct. */
export function messageVersionLabel(row: Pick<CurrentMessageRow, 'message_version'>): string {
  return row.message_version === null ? 'Version not recorded' : `Version ${row.message_version}`;
}
export function messageRate(outcomes: number, denominator: number): string {
  return denominator === 0
    ? 'Not measured'
    : `${Math.round((outcomes / denominator) * 1000) / 10}%`;
}
